import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import type { z } from 'zod';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { db } from '@/lib/firebase';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { toast } from '@/components/ui/toaster';
import { StoreSchema, type Store } from '@/types/schemas';

export function StoresPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Store | null>(null);
  const [open, setOpen] = useState(false);

  const { data: stores = [], isLoading } = useQuery({
    queryKey: ['stores'],
    queryFn: async (): Promise<Store[]> => {
      const snap = await getDocs(query(collection(db, 'stores'), orderBy('name')));
      return snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Store, 'id'>) }));
    },
  });

  const remove = useMutation({
    mutationFn: async (id: string) => {
      await deleteDoc(doc(db, 'stores', id));
    },
    onSuccess: () => {
      toast({ title: 'Store deleted', variant: 'success' });
      qc.invalidateQueries({ queryKey: ['stores'] });
    },
    onError: (e) => toast({ title: 'Delete failed', description: String(e), variant: 'destructive' }),
  });

  return (
    <div>
      <PageHeader
        title="Stores"
        subtitle="Manage your physical store locations."
        actions={
          <Button
            onClick={() => {
              setEditing(null);
              setOpen(true);
            }}
          >
            <Plus className="h-4 w-4" /> New store
          </Button>
        }
      />

      <Card>
        <CardContent className="p-0">
          {isLoading && (
            <div className="p-6 text-sm text-muted-foreground">Loading…</div>
          )}
          {!isLoading && stores.length === 0 && (
            <div className="p-10 text-center text-sm text-muted-foreground">
              No stores yet. Create your first store to start using the POS.
            </div>
          )}
          <div className="divide-y">
            {stores.map((s) => (
              <div
                key={s.id}
                className="flex items-center justify-between gap-3 px-5 py-3 text-sm active:bg-accent/40"
              >
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{s.name}</span>
                    {!s.isActive && <Badge variant="muted">inactive</Badge>}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">
                    {s.address || '—'} {s.phone ? ` · ${s.phone}` : ''}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setEditing(s);
                      setOpen(true);
                    }}
                  >
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Delete store "${s.name}"?`)) remove.mutate(s.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <StoreFormDialog
        open={open}
        onOpenChange={setOpen}
        editing={editing}
      />
    </div>
  );
}

function StoreFormDialog({
  open,
  onOpenChange,
  editing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  editing: Store | null;
}) {
  const qc = useQueryClient();
  type FormValues = z.input<typeof StoreSchema>;
  const form = useForm<FormValues>({
    resolver: zodResolver(StoreSchema),
    values: {
      name: editing?.name ?? '',
      address: editing?.address ?? '',
      phone: editing?.phone ?? '',
      isActive: editing?.isActive ?? true,
    },
  });

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      if (editing) {
        await updateDoc(doc(db, 'stores', editing.id), {
          ...values,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(collection(db, 'stores'), {
          ...values,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      }
    },
    onSuccess: () => {
      toast({ title: editing ? 'Store updated' : 'Store created', variant: 'success' });
      qc.invalidateQueries({ queryKey: ['stores'] });
      onOpenChange(false);
    },
    onError: (e) => toast({ title: 'Save failed', description: String(e), variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{editing ? 'Edit store' : 'New store'}</DialogTitle>
          <DialogDescription>
            Stores group inventory and orders by location.
          </DialogDescription>
        </DialogHeader>
        <form
          onSubmit={form.handleSubmit((v) => save.mutate(v))}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="name">Name</Label>
            <Input id="name" {...form.register('name')} />
            {form.formState.errors.name && (
              <p className="text-xs text-destructive">
                {form.formState.errors.name.message}
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="address">Address</Label>
            <Input id="address" {...form.register('address')} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Phone</Label>
            <Input id="phone" {...form.register('phone')} />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="active"
              type="checkbox"
              {...form.register('isActive')}
              className="h-4 w-4 rounded border-input"
            />
            <Label htmlFor="active" className="text-sm font-normal">
              Active
            </Label>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
